/**
 * Machines list page.
 *
 * Displays all machines in a FilterableDataTable with columns:
 * Name, Type, Status, Last Inspected, Alerts, Actions.
 *
 * Uses TanStack Query to fetch machine data from the backend and mocks
 * health status, last inspected date, and active alert counts until a
 * dedicated health summary endpoint is available.
 *
 * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridColDef } from '@mui/x-data-grid';
import MachineDrawerForm from './MachineDrawerForm';
import {
  FilterableDataTable,
  StatusBadge,
  PageContainer,
  type FilterConfig,
  type ActiveFilter,
  type RowAction,
  type HealthStatus,
} from '../../components/shared';
import { MachineControllerApi, MachineTypeControllerApi } from '../../api-generated/apis';
import type { PageResponseMachine } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const machineApi = new MachineControllerApi(apiConfig);
const machineTypeApi = new MachineTypeControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

/** Shape of a machine row in the list (API data + mocked health fields). */
interface MachineRow {
  id: string;
  name: string;
  machineType: string;
  healthStatus: HealthStatus;
  lastInspected: string;
  activeAlerts: number;
}

// ─── Filter Configuration ────────────────────────────────────────────────────

const HEALTH_STATUS_FILTER: FilterConfig = {
  field: 'healthStatus',
  label: 'Status',
  type: 'multiselect',
  options: [
    { value: 'HEALTHY', label: 'Healthy' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'CRITICAL', label: 'Critical' },
    { value: 'UNKNOWN', label: 'Unknown' },
  ],
};

// ─── Column Definitions ──────────────────────────────────────────────────────

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 150,
  },
  {
    field: 'machineType',
    headerName: 'Type',
    flex: 0.7,
    minWidth: 120,
  },
  {
    field: 'healthStatus',
    headerName: 'Status',
    flex: 0.6,
    minWidth: 130,
    renderCell: (params) => (
      <StatusBadge status={params.value as HealthStatus} />
    ),
  },
  {
    field: 'lastInspected',
    headerName: 'Last Inspected',
    flex: 0.7,
    minWidth: 130,
  },
  {
    field: 'activeAlerts',
    headerName: 'Alerts',
    flex: 0.4,
    minWidth: 80,
    renderCell: (params) => {
      const count = params.value as number;
      if (count === 0) return <Typography variant="body2">—</Typography>;
      return (
        <Chip
          label={count}
          size="small"
          color="error"
          sx={{ fontWeight: 600, minWidth: 28 }}
        />
      );
    },
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const MachinesPage: React.FC = () => {
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [, setActiveFilters] = useState<ActiveFilter[]>([]);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ─── Data fetching ───────────────────────────────────────────────────────
  const machinesQuery = useQuery<PageResponseMachine, Error>({
    queryKey: ['machines', 'list', page, pageSize],
    queryFn: () => machineApi.getAll2({ pageNumber: page, perPage: pageSize }),
  });

  // Cache all machine types for name resolution
  const machineTypesQuery = useQuery<any[]>({
    queryKey: ['machine-types'],
    queryFn: () => machineTypeApi.getAllMachineTypes(),
    staleTime: 5 * 60_000, // 5 min cache — types rarely change
  });

  // Build a lookup map: typeId → typeName
  const typeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (machineTypesQuery.data ?? []).forEach((t: any) => {
      if (t.id && t.name) map.set(t.id, t.name);
    });
    return map;
  }, [machineTypesQuery.data]);

  const filterConfigs: FilterConfig[] = useMemo(() => [
    {
      field: 'machineType',
      label: 'Type',
      type: 'select' as const,
      options: (machineTypesQuery.data ?? []).map((t: any) => ({
        value: t.name ?? '',
        label: t.name ?? '',
      })),
    },
    HEALTH_STATUS_FILTER,
  ], [machineTypesQuery.data]);

  // Transform API data into row objects
  const rows: MachineRow[] = useMemo(() => {
    const content = machinesQuery.data?.result ?? [];
    return content.map((machine: any) => {
      const machineId = machine.id ?? '';
      const typeId = machine.machineType?.id;
      const typeName = typeId ? (typeNameMap.get(typeId) ?? machine.machineType?.name ?? 'Unknown') : 'Unknown';
      return {
        id: machineId,
        name: machine.name ?? 'Unknown',
        machineType: typeName,
        healthStatus: 'UNKNOWN' as HealthStatus,
        lastInspected: '—',
        activeAlerts: 0,
      };
    });
  }, [machinesQuery.data]);

  const totalCount = machinesQuery.data?.total ?? 0;

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleRowClick = useCallback(
    (row: MachineRow) => {
      navigate(`/machines/${row.id}`);
    },
    [navigate],
  );

  const handleFilterChange = useCallback((filters: ActiveFilter[]) => {
    setActiveFilters(filters);
  }, []);

  const handleAddMachine = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  // ─── Row actions ─────────────────────────────────────────────────────────

  const rowActions: RowAction<MachineRow>[] = useMemo(
    () => [
      {
        label: 'Edit',
        icon: EditIcon,
        onClick: (row) => navigate(`/machines/${row.id}/edit`),
      },
      {
        label: 'Delete',
        icon: DeleteIcon,
        onClick: (row) => {
          // Placeholder — will be wired to a confirm dialog in a later task
          console.log('Delete machine:', row.id);
        },
      },
    ],
    [navigate],
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Machines" subtitle="Manage your fleet of machines">
      <FilterableDataTable<MachineRow>
        rows={rows}
        columns={columns}
        loading={machinesQuery.isLoading}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        filters={filterConfigs}
        onFilterChange={handleFilterChange}
        primaryAction={{ label: 'Add Machine', onClick: handleAddMachine }}
        onRowClick={handleRowClick}
        actions={rowActions}
        searchPlaceholder="Search machines…"
        testIdPrefix="machines"
      />
      <MachineDrawerForm
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        mode="create"
      />
    </PageContainer>
  );
};

export default MachinesPage;
